import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Menu,
  X,
  Users,
  Calendar,
  Shield,
  Heart,
  MessageCircle,
  ArrowRight,
  CheckCircle,
  Phone,
  Mail,
  Video,
  Lock,
  ExternalLink,
  MapPin,
  Facebook,
  Twitter,
  Linkedin,
  Youtube,
  Instagram,
  BookOpen,
  GraduationCap,
  Globe,
} from "lucide-react";
import ThemeToggle from "../common/ThemeToggle";
import { EventBanner } from "../announcements";

const Counter = ({ target }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let start = 0;
    const duration = 2000;
    const stepTime = Math.abs(Math.floor(duration / target));

    const timer = setInterval(() => {
      start += 1;
      setCount(start);
      if (start >= target) {
        clearInterval(timer);
      }
    }, stepTime);

    return () => clearInterval(timer);
  }, [target]);

  return <span>{count.toLocaleString()}</span>;
};

const Home = () => {
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="relative flex min-h-screen flex-col overflow-x-hidden bg-gradient-usiu-hero dark:bg-slate-950">
      {/* Modern Gradient Navigation */}
      <nav className="fixed right-0 left-0 top-0 shadow-lg bg-white dark:bg-slate-900/95 backdrop-blur-lg border-b border-gray-100 dark:border-gray-800 transition-all duration-300 z-50">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex justify-between items-center h-20 gap-8">
            {/* Logo and Title */}
            <Link to="/" className="flex items-center gap-3 group">
              <img
                src="/usiu-logo-02.png"
                alt="USIU Logo"
                className="h-12 w-12 object-contain"
                onError={(e) => {
                  e.target.src = "/usiu-logo.png";
                }}
              />
              <div>
                <h1 className="text-xl font-display font-bold bg-gradient-usiu-primary bg-clip-text text-transparent">
                  USIU-Africa
                </h1>
                <p className="text-xs text-gray-600 dark:text-gray-300 font-medium">Counseling & Wellbeing</p>
              </div>
            </Link>

            {/* Theme Toggle and Hamburger Button (Mobile) */}
            <div className="lg:hidden flex items-center gap-2">
              <ThemeToggle />
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="text-gray-700 dark:text-gray-200 hover:bg-usiu-blue-50 p-3 rounded-xl transition-all"
              >
                {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>

            {/* Desktop Links */}
            <div className="hidden lg:flex items-center space-x-4">
              <ThemeToggle />
              {user ? (
                <>
                  <span className="text-gray-700 dark:text-gray-200 font-medium px-3">
                    Welcome, {user.fullName || user.name}
                  </span>
                  <Link
                    to={`/${user.role}/dashboard`}
                    className="bg-gradient-usiu-primary text-white font-semibold px-4 py-2 rounded-xl hover:shadow-xl hover:shadow-usiu-blue-500/20 transform hover:scale-105 transition-all duration-200"
                  >
                    Go to Dashboard
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="text-gray-700 dark:text-gray-200 font-medium px-4 py-2 rounded-2xl hover:bg-usiu-blue-50 dark:hover:bg-slate-800 transition-all"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="bg-gradient-usiu-primary text-white font-semibold px-4 py-2 rounded-xl hover:shadow-xl hover:shadow-usiu-blue-500/20 transform hover:scale-105 transition-all duration-200"
                  >
                    Sign Up
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* Mobile Menu */}
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="lg:hidden flex flex-col space-y-3 pb-6 pt-2"
            >
              {user ? (
                <>
                  <span className="text-gray-700 dark:text-gray-200 font-medium px-4">
                    Welcome, {user.fullName || user.name}
                  </span>
                  <Link
                    to={`/${user.role}/dashboard`}
                    className="bg-gradient-usiu-primary text-white font-semibold px-4 py-2 rounded-xl text-center"
                  >
                    Go to Dashboard
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-gray-700 dark:text-gray-200 font-medium px-4 py-2 hover:bg-usiu-blue-50 dark:hover:bg-slate-800 rounded-2xl transition">
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="bg-gradient-usiu-primary text-white font-semibold px-4 py-2 rounded-xl text-center"
                  >
                    Sign Up
                  </Link>
                </>
              )}
            </motion.div>
          )}
        </div>
      </nav>

      {/* Hero Section with Background Image */}
      <section className="relative pt-32 pb-32 px-6 overflow-hidden min-h-[85vh] flex items-center">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="/counselling.jpg"
            alt="Counseling Support"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          {/* Overlay for Text Readability */}
          <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-black/40 to-black/50 dark:from-black/80 dark:via-black/70 dark:to-black/80"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10 w-full">
          {/* Centered Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-5xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold text-white leading-tight mb-6 drop-shadow-2xl">
              Mental Health{" "}
              <span className="bg-gradient-to-r from-usiu-gold-400 to-usiu-gold-500 bg-clip-text text-transparent">
                Matters
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-white drop-shadow-lg mb-12 leading-relaxed max-w-3xl mx-auto">
              USIU-Africa Counseling Center offers professional support for students and staff facing
              depression, anxiety, stress, academic pressure, trauma, relationship challenges, and personal growth.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap justify-center gap-3 mb-16">
              {!user ? (
                <>
                  <Link
                    to="/register"
                    className="bg-usiu-blue-600 text-white font-semibold px-6 py-3 rounded-2xl hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                  >
                    Get Started
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                  <Link
                    to="/login"
                    className="bg-white/90 backdrop-blur-sm text-gray-900 font-semibold px-6 py-3 rounded-2xl border-2 border-white hover:bg-white hover:shadow-lg transition-all duration-200"
                  >
                    Sign In
                  </Link>
                </>
              ) : (
                <Link
                  to={`/${user.role}/dashboard`}
                  className="bg-usiu-blue-600 text-white font-semibold px-6 py-3 rounded-2xl hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                >
                  Go to Dashboard
                  <ArrowRight className="w-5 h-5" />
                </Link>
              )}
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-white dark:bg-slate-800 backdrop-blur-md border border-gray-200 dark:border-slate-700 rounded-xl p-4 shadow-lg"
              >
                <p className="text-3xl font-display font-bold text-usiu-blue-600 dark:text-usiu-blue-400 mb-1">
                  <Counter target={1200} />+
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300">Sessions Completed</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="bg-white dark:bg-slate-800 backdrop-blur-md border border-gray-200 dark:border-slate-700 rounded-xl p-4 shadow-lg"
              >
                <p className="text-3xl font-display font-bold text-usiu-blue-600 dark:text-usiu-blue-400 mb-1">
                  <Counter target={500} />+
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300">Students & Staff Supported</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="bg-white dark:bg-slate-800 backdrop-blur-md border border-gray-200 dark:border-slate-700 rounded-xl p-4 shadow-lg"
              >
                <p className="text-3xl font-display font-bold text-usiu-blue-600 dark:text-usiu-blue-400 mb-1">
                  <Counter target={95} />%
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300">Satisfaction Rate</p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Event Announcements Banner */}
      <EventBanner />

      {/* Services Section */}
      <section className="py-20 px-6 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-display font-bold text-gray-900 dark:text-white mb-4">
              Comprehensive Counseling Services
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              We offer a range of professional mental health services tailored
              to meet diverse needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Individual Counseling */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white dark:bg-slate-800 p-8 rounded-2xl border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-xl transition-all duration-300 group"
            >
              <div className="bg-blue-100 dark:bg-blue-900/30 w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Heart className="w-7 h-7 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-xl font-display font-bold text-gray-900 dark:text-white mb-3">
                Individual Counseling
              </h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                Confidential one-on-one sessions addressing depression, anxiety, relationship issues, and personal challenges
              </p>
            </motion.div>

            {/* Group Therapy */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white dark:bg-slate-800 p-8 rounded-2xl border border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-600 hover:shadow-xl transition-all duration-300 group"
            >
              <div className="bg-indigo-100 dark:bg-indigo-900/30 w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Users className="w-7 h-7 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h3 className="text-xl font-display font-bold text-gray-900 dark:text-white mb-3">
                Group Therapy
              </h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                Supportive group sessions for stress management, grief counseling, academic pressure, and peer support
              </p>
            </motion.div>

            {/* Crisis Intervention */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-white dark:bg-slate-800 p-8 rounded-2xl border border-gray-200 dark:border-gray-700 hover:border-red-300 dark:hover:border-red-600 hover:shadow-xl transition-all duration-300 group"
            >
              <div className="bg-red-100 dark:bg-red-900/30 w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Shield className="w-7 h-7 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-xl font-display font-bold text-gray-900 dark:text-white mb-3">
                Crisis Intervention
              </h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                Professional support for mental health crises and immediate safety concerns
              </p>
            </motion.div>

            {/* Virtual Counseling */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="bg-white dark:bg-slate-800 p-8 rounded-2xl border border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600 hover:shadow-xl transition-all duration-300 group"
            >
              <div className="bg-purple-100 dark:bg-purple-900/30 w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Video className="w-7 h-7 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-xl font-display font-bold text-gray-900 dark:text-white mb-3">
                Virtual Counseling
              </h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                Flexible online sessions for remote support and convenience
              </p>
            </motion.div>

            {/* Easy Scheduling */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="bg-white dark:bg-slate-800 p-8 rounded-2xl border border-gray-200 dark:border-gray-700 hover:border-green-300 dark:hover:border-green-600 hover:shadow-xl transition-all duration-300 group"
            >
              <div className="bg-green-100 dark:bg-green-900/30 w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Calendar className="w-7 h-7 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-display font-bold text-gray-900 dark:text-white mb-3">
                Easy Scheduling
              </h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                Book appointments at your convenience with your preferred counselor
              </p>
            </motion.div>

            {/* Confidential & Secure */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="bg-white dark:bg-slate-800 p-8 rounded-2xl border border-gray-200 dark:border-gray-700 hover:border-teal-300 dark:hover:border-teal-600 hover:shadow-xl transition-all duration-300 group"
            >
              <div className="bg-teal-100 dark:bg-teal-900/30 w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Lock className="w-7 h-7 text-teal-600 dark:text-teal-400" />
              </div>
              <h3 className="text-xl font-display font-bold text-gray-900 dark:text-white mb-3">
                Confidential & Secure
              </h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                Your privacy is protected with secure, encrypted sessions and records
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-gradient-to-br from-gray-50 to-blue-50 dark:from-slate-900 dark:to-slate-800">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center space-y-6"
          >

            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 dark:text-white leading-tight">
              Ready to Take the{" "}
              <span className="bg-gradient-usiu-primary bg-clip-text text-transparent">
                First Step?
              </span>
            </h2>

            <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed max-w-2xl mx-auto">
              Your journey to better mental health starts here. Join hundreds of students and staff who have found support through our professional counseling services.
            </p>

            {/* Benefits List */}
            <div className="grid md:grid-cols-3 gap-4 pt-4 max-w-3xl mx-auto">
              <div className="flex flex-col items-center gap-2 text-center">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                <p className="font-semibold text-gray-900 dark:text-white">Confidential & Secure</p>
              </div>
              <div className="flex flex-col items-center gap-2 text-center">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                <p className="font-semibold text-gray-900 dark:text-white">Professional Counselors</p>
              </div>
              <div className="flex flex-col items-center gap-2 text-center">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                <p className="font-semibold text-gray-900 dark:text-white">Flexible Scheduling</p>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap justify-center gap-4 pt-6">
              {!user ? (
                <>
                  <Link
                    to="/register"
                    className="bg-gradient-usiu-primary text-white font-semibold px-6 py-3 rounded-2xl hover:shadow-xl hover:shadow-usiu-blue-500/30 transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                  >
                    Get Started Free
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                  <Link
                    to="/login"
                    className="bg-white dark:bg-slate-800 text-gray-900 dark:text-white font-semibold px-6 py-3 rounded-2xl border-2 border-gray-200 dark:border-gray-700 hover:border-usiu-blue-500 dark:hover:border-usiu-blue-500 hover:shadow-lg transition-all duration-200"
                  >
                    Sign In
                  </Link>
                </>
              ) : (
                <Link
                  to={`/${user.role}/appointments/new`}
                  className="bg-gradient-usiu-primary text-white font-semibold px-6 py-3 rounded-2xl hover:shadow-xl hover:shadow-usiu-blue-500/30 transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                >
                  Book an Appointment
                  <ArrowRight className="w-5 h-5" />
                </Link>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 dark:bg-slate-950 text-white py-12 px-6 border-t border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
            {/* Logo and Description */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img
                  src="/usiu-logo-02.png"
                  alt="USIU Logo"
                  className="h-12 w-12 object-contain"
                />
                <h3 className="text-xl font-display font-bold">
                  USIU-Africa Counseling Center
                </h3>
              </div>
              <p className="text-gray-400 max-w-md mb-6">
                USIU-Africa Counseling Center - Professional, confidential support
                addressing stress, depression, anxiety, relationship issues, academic
                pressure, and other mental health challenges.
              </p>
              {/* Social Media Links */}
              <div className="flex items-center gap-4">
                <a
                  href="https://www.facebook.com/USIUAFRICA"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-blue-600 transition"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a
                  href="https://twitter.com/ExperienceUSIU"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-sky-500 transition"
                >
                  <Twitter className="w-5 h-5" />
                </a>
                <a
                  href="https://www.linkedin.com/school/united-states-international-university-africa/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-blue-700 transition"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
                <a
                  href="https://www.youtube.com/channel/UC9FfQmnxT71HqR6naR9NjxQ"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-red-600 transition"
                >
                  <Youtube className="w-5 h-5" />
                </a>
                <a
                  href="https://www.instagram.com/usiuafrica"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-pink-600 transition"
                >
                  <Instagram className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-display font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-3 text-gray-400">
                {!user && (
                  <>
                    <li>
                      <Link to="/register" className="hover:text-white transition">
                        Register
                      </Link>
                    </li>
                    <li>
                      <Link to="/login" className="hover:text-white transition">
                        Login
                      </Link>
                    </li>
                  </>
                )}
              </ul>
            </div>

            {/* USIU Website */}
            <div>
              <h4 className="font-display font-semibold mb-4">USIU Website</h4>
              <ul className="space-y-3 text-gray-400">
                <li>
                  <a
                    href="https://www.usiu.ac.ke"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-white transition flex items-center gap-2"
                  >
                    <Globe className="w-4 h-4" />
                    USIU Home
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.usiu.ac.ke/academics"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-white transition flex items-center gap-2"
                  >
                    <GraduationCap className="w-4 h-4" />
                    Academics
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
                <li>
                  <a
                    href="https://studentportal.usiu.ac.ke"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-white transition flex items-center gap-2"
                  >
                    <Users className="w-4 h-4" />
                    Student Portal
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-display font-semibold mb-4">Contact Us</h4>
              <div className="space-y-3 text-gray-400">
                <a
                  href="mailto:counsel@usiu.ac.ke"
                  className="flex items-center gap-2 hover:text-white transition"
                >
                  <Mail className="w-4 h-4" />
                  counsel@usiu.ac.ke
                </a>
                <a
                  href="tel:+254730116748"
                  className="flex items-center gap-2 hover:text-white transition"
                >
                  <Phone className="w-4 h-4" />
                  +254 730 116 748
                </a>
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
                  <span>
                    USIU-Africa<br />
                    Thika Road, Kasarani<br />
                    P.O. Box 14634-00800
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-800 mt-10 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-400 text-sm text-center md:text-left">
                &copy; {new Date().getFullYear()} USIU-Africa Counseling Center. All rights reserved.
              </p>
              <div className="flex items-center gap-6 text-gray-400 text-sm">
                <Link to="/privacy-policy" className="hover:text-white transition">
                  Privacy Policy
                </Link>
                <Link to="/terms-of-use" className="hover:text-white transition">
                  Terms of Use
                </Link>
                <span>Confidential Mental Health Services</span>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
